import { ref, computed, nextTick } from "vue";

export function useSmartTagInput(props) {
    const input = ref(null);
    const text = ref(props.value || props.default);
    const charCount = ref(text.value.length);
    const showDropdown = ref(false);
    const dropdownTrigger = ref("");
    const selectedDropdownIndex = ref(-1);

    const filteredTags = computed(() => {
        if (!dropdownTrigger.value) {
            return props.tags;
        }

        return props.tags.filter((tag) =>
            tag.name.startsWith(dropdownTrigger.value)
        );
    });

    const handleInput = () => {
        const textContent = input.value.innerText;
        const lastChar = textContent.charAt(textContent.length - 1);

        if (lastChar === ":") {
            dropdownTrigger.value = "";
            showDropdown.value = true;
            selectedDropdownIndex.value = 0;
        } else if (showDropdown.value) {
            const triggerIndex = textContent.lastIndexOf(":");
            if (triggerIndex !== -1) {
                dropdownTrigger.value = textContent.substring(triggerIndex + 1);
            }
        } else {
            showDropdown.value = false;
        }

        if (props.maxChars !== null && textContent.length > props.maxChars) {
            const excess = textContent.length - props.maxChars;
            const selection = window.getSelection();
            const range = selection.getRangeAt(0);
            const start = range.startOffset - excess;
            input.value.innerText = textContent.substring(0, props.maxChars);
            charCount.value = props.maxChars;

            range.setStart(input.value.firstChild, Math.max(start, 0));
            range.setEnd(input.value.firstChild, Math.max(start, 0));
            selection.removeAllRanges();
            selection.addRange(range);
        } else {
            charCount.value = textContent.length;
        }

        text.value = textContent;
    };

    const handleKeydown = (event) => {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);

        if (
            event.key === "Enter" &&
            !showDropdown.value &&
            props.type === "input"
        ) {
            event.preventDefault();
            return;
        }

        if (showDropdown.value && filteredTags.value.length > 0) {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                selectedDropdownIndex.value =
                    (selectedDropdownIndex.value + 1) %
                    filteredTags.value.length;
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                selectedDropdownIndex.value =
                    (selectedDropdownIndex.value -
                        1 +
                        filteredTags.value.length) %
                    filteredTags.value.length;
            } else if (event.key === "Enter") {
                event.preventDefault();
                insertTagFromDropdown(
                    filteredTags.value[selectedDropdownIndex.value].name
                );
            }
        }

        if (event.key === "Backspace") {
            if (
                range.startOffset === 0 &&
                range.startContainer.previousSibling &&
                range.startContainer.previousSibling.textContent.slice(-1) ===
                    ":"
            ) {
                showDropdown.value = false;
            } else if (
                range.startOffset > 0 &&
                range.startContainer.textContent.charAt(
                    range.startOffset - 1
                ) === ":"
            ) {
                showDropdown.value = false;
            }
        }
    };

    const createSmartTagsFromString = (value) => {
        const delimiter = "__DELIM__";
        let processedValue = value;

        const replaceFirstOccurrence = (str, find, replace) => {
            const index = str.indexOf(find);
            if (index === -1) return str;
            return (
                str.substring(0, index) +
                replace +
                str.substring(index + find.length)
            );
        };

        props.tags.forEach((tag) => {
            processedValue = replaceFirstOccurrence(
                processedValue,
                `:${tag.name}`,
                `${delimiter}:${tag.name}${delimiter}`
            );
        });

        let parts = processedValue.split(delimiter);
        let html = "";

        parts.forEach((part) => {
            if (part.startsWith(":")) {
                let potentialTag = part.substring(1);
                if (props.tags.find((tag) => tag.name === potentialTag)) {
                    const tag = createSmartTag(potentialTag);
                    html += tag.outerHTML;
                } else {
                    html += part;
                }
            } else {
                html += part;
            }
        });

        return html;
    };

    const createSmartTag = (tag) => {
        const tagSpan = document.createElement("span");
        tagSpan.textContent = ":" + tag;
        tagSpan.classList.add(
            "tag",
            "bg-utility-blue-light-100",
            "text-utility-blue-light-700",
            "px-2",
            "py-0.5",
            "rounded-md",
            "text-sm",
            "font-medium"
        );
        tagSpan.contentEditable = "false";
        return tagSpan;
    };

    const insertTagFromDropdown = (tag) => {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);

        if (!input.value.contains(range.commonAncestorContainer)) {
            return;
        }

        const triggerIndex = range.startContainer.textContent.lastIndexOf(
            ":",
            range.startOffset
        );
        range.setStart(range.startContainer, triggerIndex);
        range.deleteContents();

        const tagSpan = createSmartTag(tag);

        range.insertNode(tagSpan);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);

        charCount.value = input.value.innerText.length;
        showDropdown.value = false;
        selectedDropdownIndex.value = -1;

        nextTick(() => {
            input.value.focus();
        });

        handleInput();
    };

    return {
        input,
        text,
        charCount,
        showDropdown,
        dropdownTrigger,
        selectedDropdownIndex,
        filteredTags,
        handleInput,
        handleKeydown,
        createSmartTagsFromString,
        createSmartTag,
        insertTagFromDropdown,
    };
}
